/**
 * Add or rename a department.
 *
 * A department is only a name, so the sheet is one field — but renaming one
 * is not, because people are already filed under the old spelling. The rename
 * carries them across rather than leaving them pointing at something that no
 * longer exists.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Sheet, { Group, Field } from '../Sheet.jsx';

export default function DepartmentSheet({ value, count = 0, onClose, onSaved }) {
  const { S } = useApp();
  const editing = !!value;

  const [name, setName] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    const next = name.trim();
    if (!next) { setError('Give the department a name'); return; }

    const clash = (S.depts || [])
      .some(d => d !== value && d.toLowerCase() === next.toLowerCase());
    if (clash) { setError('There is already a department with that name'); return; }

    if (editing && next === value) { onClose(); return; }

    setBusy(true);
    setError('');
    try {
      await onSaved(next);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not save');
      setBusy(false);
    }
  };

  return (
    <Sheet
      icon="building"
      tone="g-cyan"
      title={editing ? 'Rename department' : 'Add a department'}
      onClose={onClose}
      onSave={busy ? undefined : save}
      saveLabel={busy ? 'Saving…' : (editing ? 'Save changes' : 'Add department')}
    >
      <Group>Department</Group>

      <Field label="Name" wide>
        <input
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
        />
        <div className="muted">
          {editing
            ? (count
              ? `${count} ${count === 1 ? 'person is' : 'people are'} in this department. `
                + 'Renaming moves them with it.'
              : 'Nobody is in this department yet.')
            : 'It appears in the department picker when you add or edit a team member.'}
        </div>
      </Field>

      {error ? <div className="f wide"><div className="err">{error}</div></div> : null}
    </Sheet>
  );
}
